import { nanoid } from "@reduxjs/toolkit";
import docApi from "../../redux/api/DocApi";
import { getAccessToken, getUserInfoFromLocalStorage } from "./auth";
import { DocInfo } from "../../types/files";
import policyApi from "../../redux/api/PolicyApi";
import { Workgroup, WorkgroupSearchDto } from "../../types/workgroup";
import { LoginDto } from "../../types/auth";
import utilsCommon from "./common";
import { meetingDTO } from "../../types/meeting";
import { t } from "i18next";
import utilsClient from "./client";

/** 미리보기  */
export async function handleDocPreview(req: {
  permission: string;
  workgroupId: string;
  syncId: string;
  toast: any;
  t: any;
  revisionNo?: number;
  tab?: string;
  type?: "WEBUSING";
  token?: string;
  navigate?: any;
  noNeedToast?: boolean;
  uniqueId?: string;
}): Promise<string | void> {
  const user = getUserInfoFromLocalStorage() as string | "";
  const loginDto: LoginDto = JSON.parse(user);

  const token = getAccessToken() as string | "";
  const toast = req.toast;
  let resp = await docApi
    .getContentDetail({ workgroupId: req.workgroupId, syncId: req.syncId })
    .then((result) => {
      /** error case 1 : 삭제된 문서일 경우 */
      if (
        result.documentInfo.status &&
        ["T", "D"].includes(result.documentInfo.status)
      ) {
        toast.toastMsg(nanoid(), t("file.alert.contentDelete"), "error");
        if (req.type === "WEBUSING")
          setTimeout(
            () =>
              req.navigate(
                utilsCommon.renderProperNavigatePath(
                  loginDto?.ecoSystemLicenseStats?.featureAccessType === "LITE"
                    ? `/myInbox/received`
                    : "/home"
                )
              ),
            3000
          );
        return "FILE_DELETED";
      }
      /** error case 2 : 문서 권한이 없는 문서일 경우 */
      if (!result || !result.documentInfo || !result.documentInfo.myPolicy) {
        !req.noNeedToast &&
          toast.toastMsg(nanoid(), t("file.alert.noPermissionView"), "error");
        return "NO_PERMISSION";
      }
      /** error case 3 : 웹에서 열람 실패한 경우 */
      if (result.documentInfo.myPolicy?.policy?.webView === null) {
        !req.noNeedToast &&
          toast.toastMsg(nanoid(), t("file.alert.previewFail"), "error");
        return "FAIL_WEBVIEW";
      }
      /** error case 4 : 웹 사용을 지원하지 않는 경우 */
      if (!result.documentInfo.myPolicy?.policy?.webView) {
        !req.noNeedToast &&
          toast.toastMsg(nanoid(), t("file.alert.noWebviewAllow"), "error");
        return "NO_WEBVIEW_PERM";
      }
      const newOpen = window.open(
        `/docs/preview?permission=${req.permission}&workgroupId=${req.workgroupId}&syncId=${req.syncId}&revisionNo=${req.revisionNo}&isClient=false&token=${token}&startTab=${req.tab}&uniqueId=${req.uniqueId}`,
        "",
        "status=no, toolbar=no, location=no, menubar=no, resizable=yes, width=1440, height=720"
      );
      newOpen?.resizeTo(1440, 720);
      return "SUCCESS";
    });
  return resp;
}
export async function gotoEditMode(
  workgroupId: string,
  syncId: string,
  toast: any
): Promise<void> {
  const newSyncInfo = await docApi.getDocSyncInfo({ workgroupId, syncId });

  if (
    newSyncInfo?.status === "N" &&
    newSyncInfo?.authType === "R" &&
    newSyncInfo?.checkoutUser.userId === ""
  ) {
    window.open(
      `/docs/preview?permission=edit&workgroupId=${workgroupId}&syncId=${syncId}&revisionNo=${newSyncInfo.revisionNo}&isClient=false`
    );
  } else {
    // await getSyncInfo({ workgroupId, syncId }); //TODO :  state setting

    if (newSyncInfo?.status === "T") {
      toast.toastMsg(nanoid(), t("file.alert.contentDelete"), "error");
    } else if (newSyncInfo?.authType === "V" || newSyncInfo.authType !== "R") {
      toast.toastMsg(nanoid(), t("file.alert.checkoutFail"), "error");
    } else if (newSyncInfo?.checkoutUser.userId !== "") {
      toast.toastMsg(nanoid(), t("file.alert.checkoutAnotherUser"), "error");
    }
  }
  //권한 체크
}

/** 암호화 해제 다운로드 */
export async function doUnauthDocDownload(
  docInfo: DocInfo,
  toast: any,
  getDocDecryptDownload: any,
  startDownload: any
) {
  if (!docInfo) return;

  let resp = await getDocDecryptDownload({
    syncId: docInfo?.syncId,
    startDownload: startDownload,
    revisionNo: docInfo?.revisionNo + "",
    contentName: docInfo?.contentName,
  });
  if (utilsCommon.isClientLogin("MAC")) return;
  if (resp.payload) {
    if (resp.payload.error) {
      if (
        resp.payload.error.name &&
        resp.payload.error.name !== "INTERNAL_SERVER_ERROR"
      ) {
        toast.toastMsg(nanoid(), resp.payload.error.message, "error");
      } else {
        toast.toastMsg(nanoid(), t("file.alert.downloadFail"), "error");
      }
    } else {
      toast.toastMsg(nanoid(), t("file.alert.downloadStart"), "success");
    }
  } else {
    toast.toastMsg(nanoid(), t("file.alert.downloadFail"), "error");
  }
}

/** 파일 다운로드 */
export async function handleDocDownload(
  syncId: string,
  getContentDownload: any,
  toast: any,
  t: any,
  revisionNo?: string,
  contentName?: string,
  noNeedToast?: boolean
) {
  let isStart = false;
  const startDownload = (loaded: number): void => {
    if (loaded > 50000000 && !isStart) {
      isStart = true;
      toast.toastMsg(nanoid(), t("file.alert.downloadBigFile"), "success");
    }
    return;
  };
  let resp = await getContentDownload({
    syncId: syncId,
    revisionNo: revisionNo,
    contentName: contentName,
    startDownload: startDownload,
  });
  if (resp.payload && !utilsCommon.isClientLogin("MAC")) {
    if (resp.payload.error) {
      if (window.eco?.serviceType === "DEMO") return false;
      if (resp.payload.error.message === "NOT_FOUND_POLICY") {
        !noNeedToast &&
          toast.toastMsg(
            nanoid(),
            t("file.alert.noPermissionDownload"),
            "error"
          );
      } else if (resp.payload?.error?.name !== "INTERNAL_SERVER_ERROR") {
        !noNeedToast &&
          toast.toastMsg(
            nanoid(),
            resp.payload.error.message ?? t("file.alert.downloadFail"),
            "error"
          );
      } else {
        !noNeedToast &&
          toast.toastMsg(nanoid(), t("file.alert.downloadFail"), "error");
      }
      return false;
    } else {
      !noNeedToast &&
        toast.toastMsg(nanoid(), t("file.alert.downloadStart"), "success");
      return true;
    }
  } else if (!utilsCommon.isClientLogin("MAC")) {
    !noNeedToast &&
      toast.toastMsg(nanoid(), t("file.alert.downloadFail"), "error");
    return false;
  }
}
/** 파일 삭제 */
export async function doDeleteDocument(
  docInfo: DocInfo[],
  toast: any,
  getDocDelete: any,
  handleDeleteAfter?: any
) {
  let resp = await getDocDelete(docInfo.map((item) => item.syncId));
  if (resp.payload) {
    handleDeleteAfter && handleDeleteAfter(docInfo.map((item) => item.syncId));
    toast.toastMsg(nanoid(), t("file.alert.deleteDoc"), "success");
  } else {
    toast.toastMsg(nanoid(), t("file.alert.deleteDocFail"), "error");
  }
}
/** 파일 링크 즉시 복사 */
export async function handleCopyLink(syncId: string, toast: any) {
  const copy = require("clipboard-copy");
  let resp: any = await policyApi.getFileLinkPolicy({
    syncId,
    workgroupId: syncId,
  });
  if (resp) {
    if (utilsCommon.isClientLogin("MAC")) {
      utilsClient.copyLink(resp.actionUrl);
    } else {
      copy(resp.actionUrl);
    }
    toast.toastMsg(nanoid(), t("file.link.copySuccess"), "success");
  } else {
    toast.toastMsg(
      nanoid(),
      resp.error.message ?? t("common.alert.fail"),
      "error"
    );
  }
}
/** 워크그룹 즐겨찾기 추가/해제 */
export async function handleSetFavorite(
  workgroupId?: string,
  updateWorkgroupFavorite?: any,
  workgroupList?: Workgroup[]
) {
  const user = getUserInfoFromLocalStorage() as string | "";
  const loginDto: LoginDto = JSON.parse(user);

  if (!workgroupId) return;
  const currFav = workgroupList?.filter(
    (item) => item.workgroupId === workgroupId
  )[0].favorite;
  updateWorkgroupFavorite({
    workgroupId,
    users: [
      {
        userId: loginDto.userInfo.userId,
        favorite: currFav === "Y" ? "N" : "Y",
      },
    ],
  });
}
/** 워크그룹 나가기 */
export async function handleExitWorkgroup(
  withdrawMember: any,
  toast: any,
  navigate: any,
  handleTurnOffSettng?: () => void,
  workgroupInfo?: Workgroup,
  getWorkgroupSideList?: (filter: WorkgroupSearchDto) => void,
  filter?: WorkgroupSearchDto
) {
  const user = getUserInfoFromLocalStorage() as string | "";
  const loginDto: LoginDto = JSON.parse(user);

  if (!workgroupInfo) return;
  if (window.location.pathname.includes(workgroupInfo.workgroupId)) {
    navigate(
      utilsCommon.renderProperNavigatePath(
        loginDto?.ecoSystemLicenseStats?.featureAccessType === "LITE"
          ? `/myInbox/received`
          : "/home"
      )
    );
  }
  utilsCommon.sleep(2000);

  let resp: any = await withdrawMember({
    workgroupId: workgroupInfo?.workgroupId,
    users: [
      {
        userId: loginDto.userInfo.userId,
        userType: loginDto.ecoUserInfo.userType,
        withdrawYN: "Y",
      },
    ],
  });
  if (resp.payload) {
    toast.toastMsg(
      nanoid(),
      t("workgroup.leaveSuccess", {
        workgroup:
          workgroupInfo?.workgroupName.length > 50
            ? workgroupInfo?.workgroupName.slice(0, 50) + "..."
            : workgroupInfo?.workgroupName,
      }),
      "success"
    );
    getWorkgroupSideList && filter && getWorkgroupSideList(filter);
  } else if (resp.error) {
    toast.toastMsg(
      nanoid(),
      resp.error.message ??
        t("workgroup.leaveFail", {
          workgroup:
            workgroupInfo?.workgroupName.length > 50
              ? workgroupInfo?.workgroupName.slice(0, 50) + "..."
              : workgroupInfo?.workgroupName,
        }),
      "error"
    );
  }
  handleTurnOffSettng && handleTurnOffSettng();
}
export const startZoomMeeting = (meetingInfo?: meetingDTO, toasts?: any) => {
  if (utilsCommon.isDemoEco()) {
    toasts.toastMsg(nanoid(), t("common.noDemoVersion"), "error");
    return;
  }
  meetingInfo?.ownerId?.toLowerCase() === utilsCommon.getMyId()
    ? window.open(meetingInfo?.startUrl)
    : window.open(meetingInfo?.joinUrl);
};
// 문서 즐겨찾기 추가
export const handleSetContentFavorite = async (req: {
  docInfos?: DocInfo[];
  flag?: boolean;
  getContentFavorite: any;
  page?: string;
  handleAfterSubmit?: any;
}) => {
  if (!req.docInfos) return;
  let resp = await req.getContentFavorite({
    syncIds: req.docInfos.map((item) => item.syncId),
    flag: req.flag ?? req.docInfos[0]?.favoritDate <= 0,
    page: req.page,
  });
  if (resp) {
    req.handleAfterSubmit && req.handleAfterSubmit();
  }
};
export const handleDocsDownload = async (req: {
  syncIds: string[];
  toast: any;
  getContentsDownload: any;
}) => {
  const { toast } = req;
  const startDownload = (loaded: number): void => {
    if (loaded > 50000000) {
      toast.toastMsg(nanoid(), t("file.alert.downloadBigFile"), "success");
    }
    return;
  };
  const checkResp = await docApi.docsDownloadCheck(req.syncIds);
  if (
    !checkResp ||
    !checkResp.data ||
    (checkResp.data.length > 0 && req.syncIds.length === checkResp.data.length)
  ) {
    toast.toastMsg(nanoid(), t("file.alert.noPermissionDownload"), "error");
    return;
  }
  let resp = await req.getContentsDownload({
    syncIds: req.syncIds,
    startDownload: startDownload,
  });
  if (resp.payload) {
    if (resp.payload.error) {
      if (
        resp.payload.error.name &&
        resp.payload.error.name !== "INTERNAL_SERVER_ERROR"
      ) {
        toast.toastMsg(nanoid(), resp.payload.error.message, "error");
      } else {
        toast.toastMsg(nanoid(), t("file.alert.downloadFail"), "error");
      }
    } else {
      toast.toastMsg(
        nanoid(),
        req.syncIds.length === 1
          ? t("file.alert.downloadStart")
          : t("file.alert.docDownload", { name: "download.zip" }),
        "success"
      );
    }
  } else {
    toast.toastMsg(nanoid(), t("file.alert.downloadFail"), "error");
  }
};
export const uploadToAIAssistant = async (req: {
  docs: DocInfo[];
  checkAIDocPermission: any;
  changeDragItem: any;
  toast: any;
}) => {
  const { docs, checkAIDocPermission, changeDragItem, toast } = req;
  /** 에러1: 중복 파일 */
  const filteredFiles = handleRemoveDuplicate(docs);
  if (filteredFiles.length !== docs.length) {
    toast.toastMsg(nanoid(), t("file.aiChat.alert.ducplicateFiles"), "error");
    return false;
  }
  /** 에러2: 지원하지 않는 AI 확장자 */
  if (
    filteredFiles.filter(
      (item: any) =>
        !utilsCommon.checkExtensionAvailable(
          item.contentName,
          "aiChatExtensions"
        )
    ).length > 0
  ) {
    toast.toastMsg(nanoid(), t("file.aiChat.alert.noSupportExt"), "error");
    return false;
  }

  const resp: any = await checkAIDocPermission(
    filteredFiles.map((item: any) => item.syncId)
  );
  /** 에러3: 삭제된 파일 */
  if (resp.error && resp.error.name === "DELETED") {
    toast.toastMsg(nanoid(), t("file.aiChat.alert.deleted"), "error");
    return false;
  }
  /** 에러4: 권한이 없는 파일 */
  if (resp.error) {
    toast.toastMsg(nanoid(), t("file.aiChat.alert.noAuth"), "error");
    return false;
  }
  changeDragItem(
    filteredFiles.filter((item: any) =>
      utilsCommon.checkExtensionAvailable(item.contentName, "aiChatExtensions")
    ),
    true
  );
};
const handleRemoveDuplicate = (files: DocInfo[]): DocInfo[] => {
  const returnSyncId = [] as string[];
  return files.filter((item) => {
    const isDuplicate = returnSyncId.includes(item.syncId);
    if (!isDuplicate) {
      returnSyncId.push(item.syncId);
      return item;
    }
  });
};
